import { useCallback, useEffect, useRef, useState } from 'react';

import {
  LoyaltyOstType,
  useCreateOrderSelectionByTypeMutation,
  useCreateOrderSelectionForRewardTierMutation,
} from 'generated/graphql-gateway';
import { useLocalStorageState } from 'hooks/use-local-storage-state';
import { useLoyaltyOffersEvaluation } from 'state/loyalty/hooks/use-loyalty-offers-evaluation';
import { StorageKeys } from 'utils/local-storage';
import logger from 'utils/logger';

import useStaticIdentifier from '../use-static-identifier';

import { IGetNewShortCodeParams, IShortCodeData, IUserShortCode, ShortCodeState } from './types';
import { differenceMsFromNow, isExpired } from './utils';

/**
 * useShortCode holds otp short-code and its state.
 */
export const useShortCode = (): IUserShortCode => {
  const { isStaticIdentifierEnabled } = useStaticIdentifier();

  const [generateOrderSelection] = useCreateOrderSelectionByTypeMutation({
    onCompleted: ({ loyaltyCreateOrderSelectionByType }) => {
      const { key: newShortCode, expirationTime } = loyaltyCreateOrderSelectionByType;

      setShortCodeData({
        shortCode: newShortCode,
        expiry: expirationTime,
      });
      setShortCodeState(ShortCodeState.Pending);
    },
    onError: () => {
      setRequestError();
    },
  });

  const [generateRewardTierOrderSelection] = useCreateOrderSelectionForRewardTierMutation({
    onCompleted: ({ createOrderSelectionForRewardTier }) => {
      const { key: newShortCode, expirationTime } = createOrderSelectionForRewardTier;

      setShortCodeData({
        shortCode: newShortCode,
        expiry: expirationTime,
      });
      setShortCodeState(ShortCodeState.Pending);
    },
    onError: () => {
      setRequestError();
    },
  });

  const { evaluateLoyaltyOffers } = useLoyaltyOffersEvaluation();
  const generateCodeOtpTimeoutRef = useRef<NodeJS.Timeout | null>();
  const abortControllerRef = useRef<AbortController>();
  const [shortCodeLoading, setShortCodeLoading] = useState<boolean>(false);
  const [{ shortCode, expiry }, setShortCodeData, clearShortCodeData] =
    useLocalStorageState<IShortCodeData>({
      key: StorageKeys.IN_RESTAURANT_REDEMPTION_SHORT_CODE,
      defaultReturnValue: {},
    });
  const [shortCodeState, setShortCodeState] = useState<ShortCodeState>(() =>
    shortCode ? ShortCodeState.Pending : ShortCodeState.None
  );
  const shouldEvaluateIncentivesRef = useRef<boolean>();
  // evaluate incentives only if the short code is expired to avoid unnecessary requests
  shouldEvaluateIncentivesRef.current = shortCodeState === ShortCodeState.Expired;

  const setRequestError = useCallback(() => {
    clearShortCodeData();
    setShortCodeState(ShortCodeState.RequestError);
  }, [clearShortCodeData]);

  const setShortCodeClaimed = useCallback(() => {
    setShortCodeState(ShortCodeState.Claimed);
  }, []);

  const setShortCodeExpired = useCallback(() => {
    setShortCodeState(ShortCodeState.Expired);
  }, []);

  const resetShortCode = useCallback(() => {
    clearShortCodeData();
    setShortCodeState(ShortCodeState.None);
    setShortCodeLoading(false);
  }, [clearShortCodeData]);

  const setIncentiveValidationError = useCallback(() => {
    setShortCodeState(ShortCodeState.OfferValidationError);
  }, [setShortCodeState]);

  /**
   * getNewShortCode works as a debounce function in order to prevent running
   * multiple mutations at the same time
   */
  const getNewShortCode = useCallback(
    (params: IGetNewShortCodeParams) => {
      const { loyaltyId, restaurantId } = params;
      if (generateCodeOtpTimeoutRef.current) {
        clearTimeout(generateCodeOtpTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      setShortCodeLoading(true);
      generateCodeOtpTimeoutRef.current = global.setTimeout(async () => {
        const controller = window.AbortController ? new window.AbortController() : undefined;
        abortControllerRef.current = controller;

        // flag to avoid set the state if the request is aborted
        let isRequestAborted = false;
        if (controller?.signal) {
          controller.signal.onabort = () => {
            isRequestAborted = true;
          };
        }
        if ('rewardTierId' in params) {
          const { rewardTierId } = params;
          try {
            await generateRewardTierOrderSelection({
              variables: {
                loyaltyId,
                incentive: rewardTierId,
                restaurantId,
              },
              context: {
                fetchOptions: {
                  signal: controller?.signal,
                },
              },
            });
          } catch (e) {
            logger.error(`Error generating a short code: ${e}`);
          } finally {
            generateCodeOtpTimeoutRef.current = null;
            setShortCodeLoading(false);
          }
          return;
        }

        const { appliedOffers, inRestaurantOrder, serviceMode } = params;

        try {
          // Incentive validations
          let hasValidationErrors = false;
          if (shouldEvaluateIncentivesRef.current && appliedOffers.length) {
            const { incentiveFeedbackMap: evaluationResult } = await evaluateLoyaltyOffers({
              appliedOffers,
              loyaltyId,
              storeId: restaurantId,
              serviceMode,
            });

            // skip validation if the request was aborted to prevent set state
            if (!isRequestAborted && evaluationResult) {
              hasValidationErrors = appliedOffers.some(
                // @ts-expect-error TS(2345) FIXME: Argument of type '({ id }: { id: string; }) => boo... Remove this comment to see the full error message
                ({ id }: { id: string }) => !!evaluationResult[id]
              );

              if (hasValidationErrors) {
                setIncentiveValidationError();
              }
            }
          }

          if (!hasValidationErrors) {
            await generateOrderSelection({
              variables: {
                inRestaurantOrder,
                restaurantId,
                loyaltyId,
                ostType: isStaticIdentifierEnabled ? LoyaltyOstType.LOYALTY_ID : LoyaltyOstType.OTP,
              },
              context: {
                fetchOptions: {
                  signal: controller?.signal,
                },
              },
            });
          }
        } catch (e) {
          logger.error(`Error generating a short code: ${e}`);
        } finally {
          generateCodeOtpTimeoutRef.current = null;
          setShortCodeLoading(false);
        }
      }, 250);
    },
    [
      evaluateLoyaltyOffers,
      generateOrderSelection,
      isStaticIdentifierEnabled,
      setIncentiveValidationError,
    ]
  );

  useEffect(() => {
    if (!expiry) {
      return;
    } else if (isExpired(expiry)) {
      setShortCodeExpired();
      return;
    }

    const codeDurationMilliseconds = differenceMsFromNow(expiry);
    const timeoutId = setTimeout(setShortCodeExpired, codeDurationMilliseconds);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [expiry, setShortCodeExpired]);

  return {
    resetShortCode,
    getNewShortCode,
    setShortCodeClaimed,
    setShortCodeExpired,
    shortCode,
    shortCodeLoading,
    shortCodeState,
  };
};
